import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Compass, PenLine, MapPin, Share2, Sparkles, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '../store/useStore';
import { useI18n } from '../i18n';
import { useTheme } from '../themes/ThemeProvider';

interface OnboardingStep {
  icon: React.ReactNode;
  titleKey: keyof typeof stepTitles;
  color: string;
  bg: string;
  gradient: string;
}

const stepTitles = {
  step1: 'step1',
  step2: 'step2',
  step3: 'step3',
  step4: 'step4',
  step5: 'step5',
} as const;

const stepConfigs: OnboardingStep[] = [
  {
    icon: <Compass className="w-10 h-10 sm:w-12 sm:h-12" />,
    titleKey: 'step1',
    color: 'text-white',
    bg: 'bg-gradient-to-br from-brand-500 to-brand-700',
    gradient: 'from-brand-600/20 to-brand-800/20',
  },
  {
    icon: <PenLine className="w-10 h-10 sm:w-12 sm:h-12" />,
    titleKey: 'step2',
    color: 'text-white',
    bg: 'bg-gradient-to-br from-green-500 to-green-700',
    gradient: 'from-green-600/20 to-green-800/20',
  },
  {
    icon: <MapPin className="w-10 h-10 sm:w-12 sm:h-12" />,
    titleKey: 'step3',
    color: 'text-white',
    bg: 'bg-gradient-to-br from-red-500 to-red-700',
    gradient: 'from-red-600/20 to-red-800/20',
  },
  {
    icon: <Zap className="w-10 h-10 sm:w-12 sm:h-12" />,
    titleKey: 'step4',
    color: 'text-white',
    bg: 'bg-gradient-to-br from-purple-500 to-purple-700',
    gradient: 'from-purple-600/20 to-purple-800/20',
  },
  {
    icon: <Sparkles className="w-10 h-10 sm:w-12 sm:h-12" />,
    titleKey: 'step5',
    color: 'text-white',
    bg: 'bg-gradient-to-br from-amber-500 to-amber-700',
    gradient: 'from-amber-600/20 to-amber-800/20',
  },
];

interface OnboardingProps {
  onComplete: () => void;
}

const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const { darkMode, uiTheme } = useStore();
  const { t } = useI18n();
  const { themeClass } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleNext = () => {
    if (currentStep < stepConfigs.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    setTimeout(() => {
      onComplete();
    }, 300);
  };

  const step = stepConfigs[currentStep];
  const stepNum = currentStep + 1;
  const titleKey = `step${stepNum}Title` as keyof typeof t.onboarding;
  const descKey = `step${stepNum}Desc` as keyof typeof t.onboarding;

  // First step uses welcome text
  const title = currentStep === 0 ? t.onboarding.welcome : t.onboarding[titleKey];
  const description = currentStep === 0 ? t.onboarding.welcomeDesc : t.onboarding[descKey];
  const highlight = currentStep === 0 ? t.onboarding.tagline : null;

  return (
    <div className={`fixed inset-0 z-[200] flex items-center justify-center p-4 ${themeClass}`}>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-neutral-950/90 backdrop-blur-xl"
      />

      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className={`relative max-w-lg w-full shadow-2xl overflow-hidden border modal-content ${
          uiTheme === 'neubrutalism' 
            ? 'rounded-none' 
            : 'rounded-3xl sm:rounded-[40px]'
        } ${darkMode ? 'bg-neutral-900 border-neutral-700' : 'bg-white border-neutral-200'}`}
      >
        {/* Background gradient */}
        <div className={`absolute inset-0 bg-gradient-to-br ${step.gradient} pointer-events-none transition-all duration-500`} />
        
        {/* Skip button */}
        <button
          onClick={handleComplete}
          className={`absolute top-6 right-6 sm:top-8 sm:right-8 p-2.5 rounded-xl z-10 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center transition-all ${darkMode ? 'text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800' : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'}`}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative p-6 sm:p-10 md:p-14">
          {/* Progress indicator */}
          <div className="flex justify-center gap-2 mb-8 sm:mb-10">
            {stepConfigs.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`h-2 rounded-full transition-all duration-500 touch-manipulation ${
                  idx === currentStep 
                    ? 'w-8 bg-brand-600' 
                    : idx < currentStep 
                      ? 'w-3 bg-brand-400' 
                      : `w-3 ${darkMode ? 'bg-neutral-700' : 'bg-neutral-300'}`
                }`}
              />
            ))}
          </div>

          {/* Step counter */}
          <div className={`text-center mb-4 text-xs font-bold uppercase tracking-widest ${darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>
            Step {stepNum} of {stepConfigs.length}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="text-center"
            >
              {/* Icon */}
              <motion.div 
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-6 sm:mb-8 shadow-xl ${step.bg} ${step.color}`}
              >
                {step.icon}
              </motion.div>

              {/* Title */}
              <h2 className={`text-2xl sm:text-3xl font-black tracking-tight mb-4 leading-tight ${darkMode ? 'text-neutral-100' : 'text-neutral-900'}`}>
                {title}
              </h2>

              {/* Description */}
              <p className={`text-base sm:text-lg mb-6 leading-relaxed max-w-sm mx-auto ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                {description}
              </p>

              {/* Highlight badge */}
              {highlight && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-block px-5 py-2.5 bg-brand-600 rounded-full shadow-lg shadow-brand-600/30"
                >
                  <p className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white">
                    {highlight}
                  </p>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-4 mt-10 sm:mt-12">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all touch-manipulation min-h-[48px] ${
                currentStep === 0
                  ? 'opacity-0 pointer-events-none'
                  : darkMode 
                    ? 'text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800' 
                    : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{t.common.back}</span>
            </button>

            <button
              onClick={handleNext}
              className="flex-1 sm:flex-none btn-primary flex items-center justify-center gap-2 px-8 py-3.5 shadow-xl shadow-brand-500/30 touch-manipulation min-h-[52px] text-base font-bold"
            >
              <span>{currentStep === stepConfigs.length - 1 ? t.onboarding.getStarted : t.common.next}</span>
              {currentStep < stepConfigs.length - 1 && <ChevronRight className="w-5 h-5" />}
            </button>
          </div>

          {/* Skip link */}
          <button
            onClick={handleComplete}
            className={`block w-full text-center mt-8 text-xs font-bold uppercase tracking-widest transition-colors touch-manipulation py-2 ${darkMode ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-400 hover:text-neutral-600'}`}
          >
            {t.onboarding.skip}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Onboarding;
